import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import WhyQap from "../components/WhyQap";
import HowItWorks from "../components/HowItWorks";
import ProductPreview from "../components/ProductPreview";
import CTASection from "../components/CTASection";
import AboutUs from "../components/AboutUs";
import ContactUs from "../components/ContactUs";
import Footer from "../components/Footer";
import reportMockup from "../utils/qap-report-mockup.png";

function LandingPage() {
  const navigate = useNavigate();

  const goToLogin = () => navigate("/login");
  const goToSignup = () => navigate("/signup");
  const scrollToContact = () =>
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  // Animation Variants for the Hero Text (Staggered)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // Animation for sections appearing on scroll
  const scrollVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const heroHighlights = [
    "Unlimited entity consolidation",
    "One-click Excel export",
    "Live QuickBooks sync",
  ];

  return (
    <div className="landing-root">
      {/* ================= HERO ================= */}
      <section className="hero">
        <motion.div
          className="hero-text"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.span className="hero-eyebrow" variants={itemVariants}>
            Built for multi-entity finance teams
          </motion.span>

          <motion.h1 variants={itemVariants}>
            Turn Complex Financials <br />
            <span className="gradient-text">Into Clear Decisions</span>
          </motion.h1>

          <motion.p variants={itemVariants}>
            QAP Financials gives finance teams instant, consolidated reporting
            across multiple entities — with powerful month-over-month
            insights, granular line-item control, and export-ready reports
            connected directly to QuickBooks Online.
          </motion.p>

          <motion.div className="hero-actions" variants={itemVariants}>
            <button className="primary-btn" onClick={goToSignup}>
              Sign Up Free
            </button>
            <button className="secondary-btn" onClick={goToLogin}>
              Log In
            </button>
            <button
              className="ghost-link"
              onClick={() =>
                document.getElementById("why")?.scrollIntoView({ behavior: "smooth" })
              }
            >
              See how it works ↓
            </button>
          </motion.div>

          <motion.ul className="hero-highlights" variants={itemVariants}>
            {heroHighlights.map((item) => (
              <li key={item}>
                <span className="hero-highlight-check">✓</span>
                {item}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          className="mockup-wrapper"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
        >
          <div className="hero-right">
            <div className="hero-mockup-frame">
              <img
                src={reportMockup}
                alt="QAP consolidated financial report dashboard"
                className="hero-mockup"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ================= SCROLL-ANIMATED SECTIONS ================= */}

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={scrollVariants}
      >
        <WhyQap />
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={scrollVariants}
      >
        <HowItWorks />
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={scrollVariants}
      >
        <ProductPreview />
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={scrollVariants}
      >
        <AboutUs />
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={scrollVariants}
      >
        <ContactUs />
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={scrollVariants}
      >
        <CTASection onSignup={goToSignup} onBookDemo={scrollToContact} />
      </motion.div>

      <Footer onSignup={goToSignup} onBookDemo={scrollToContact} />
    </div>
  );
}

export default LandingPage;
